/**
 * Realtime client contract (V1-05).
 *
 * RealtimeStore consumes ONLY this surface, so the transport is swappable:
 * the production EchoClient (laravel-echo + pusher-js) and the CI-safe
 * FakeReverbClient (src/test/realtime/fake-reverb.ts) both implement it —
 * the fake's header documents this exact contract and both must stay
 * structurally identical.
 *
 * Channel: `orders.{organizationId}` — PUBLIC, no auth request (mirrors
 * pos-app/src/services/websocketService.ts:84-101).
 * Events: exactly `NewOrderReceived` and `OrderStatusChanged`, broadcast
 * with BARE names (no Laravel namespace) — see echo-client.ts for the
 * leading-dot handling.
 */

export type ConnectionState =
  'initialized' | 'connecting' | 'connected' | 'unavailable' | 'failed' | 'disconnected';

export type EventCallback = (payload: unknown) => void;
export type ConnectionCallback = (payload: unknown) => void;

export interface ChannelLike {
  name: string;
  listen(event: string, callback: EventCallback): ChannelLike;
  /** pusher-js spelling of listen(). */
  bind(event: string, callback: EventCallback): ChannelLike;
  stopListening(event: string, callback?: EventCallback): ChannelLike;
  unbind(event?: string, callback?: EventCallback): ChannelLike;
}

export interface RealtimeClientLike {
  subscribe(channelName: string): ChannelLike;
  unsubscribe(channelName: string): void;
  disconnect(): void;
  connection: {
    state: ConnectionState;
    bind(
      event: 'state_change' | 'connected' | 'disconnected' | 'error',
      cb: ConnectionCallback,
    ): void;
    unbind(event?: string, cb?: ConnectionCallback): void;
  };
}

/** The ONLY channel pattern V1 subscribes to (06-realtime.md §3). */
export function ordersChannel(organizationId: number): string {
  return `orders.${organizationId}`;
}

/** The two existing events — do not bind anything else (06-realtime.md §3). */
export const NEW_ORDER_EVENT = 'NewOrderReceived';
export const ORDER_STATUS_EVENT = 'OrderStatusChanged';
